import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import Cookies from "js-cookie";
import Router from "next/router";
import { computeOfferTypesFromAddressOfferType, DEFAULT_ADDRESS_OFFER_TYPE, OfferTypes } from "~/helpers/getOfferTypes";
import useIsomorphicLayoutEffect from "~/hooks/useIsomorphicLayoutEffect";

export type AddressOfferTypeContextValue = {
  addressOfferType: string;
  offerTypes: OfferTypes;
};

const AddressOfferTypeContext = createContext<AddressOfferTypeContextValue | null>(null);

function useAddressOfferTypeState(): AddressOfferTypeContextValue {
  const [addressOfferType, setAddressOfferType] = useState(DEFAULT_ADDRESS_OFFER_TYPE);

  const syncAddressOfferType = useCallback(() => {
    const nextAddressOfferType = Cookies.get("addressOfferType") ?? DEFAULT_ADDRESS_OFFER_TYPE;

    setAddressOfferType((currentAddressOfferType) =>
      currentAddressOfferType === nextAddressOfferType ? currentAddressOfferType : nextAddressOfferType
    );
  }, []);

  useIsomorphicLayoutEffect(() => {
    syncAddressOfferType();

    // Keep the provider in sync with cookie updates that happen during SPA navigation.
    Router?.events?.on("routeChangeComplete", syncAddressOfferType);

    return () => {
      Router?.events?.off("routeChangeComplete", syncAddressOfferType);
    };
  }, [syncAddressOfferType]);

  const offerTypes = useMemo(() => computeOfferTypesFromAddressOfferType(addressOfferType), [addressOfferType]);

  return { addressOfferType, offerTypes };
}

export function AddressOfferTypeProvider({ children }: { children: ReactNode }) {
  const value = useAddressOfferTypeState();
  return React.createElement(AddressOfferTypeContext.Provider, { value }, children);
}

export function useAddressOfferType(): AddressOfferTypeContextValue {
  const ctx = useContext(AddressOfferTypeContext);
  if (!ctx) {
    throw new Error("useAddressOfferType must be used within an AddressOfferTypeProvider");
  }
  return ctx;
}
